import { nearestLengthOnPath } from '@/lib/shapes/text-on-path-session'
import { forwardDistanceFor, resolveTextOnPathDirection } from '@/lib/shapes/text-on-path-direction'

export type TextOnPathDirection = ReturnType<typeof resolveTextOnPathDirection>

/**
 * Screen point → the path's own SVG user space → the nearest start distance along the path.
 *
 * `getScreenCTM()` handles the whole canvas-zoom/viewBox chain that a hand-rolled screen→canvas
 * conversion would have to re-derive. Returns `null` when the environment has no SVG geometry
 * (jsdom) or the element isn't attached to an `<svg>`.
 */
export function projectClientPointToStartDistance(
  pathElement: SVGPathElement | null,
  clientX: number,
  clientY: number,
  pathLength: number,
  direction: TextOnPathDirection
): number | null {
  const svg = pathElement?.ownerSVGElement

  if (!pathElement || !svg || typeof pathElement.getPointAtLength !== 'function') {
    return null
  }

  if (typeof svg.createSVGPoint !== 'function' || typeof svg.getScreenCTM !== 'function') {
    return null
  }

  const ctm = svg.getScreenCTM()

  if (!ctm) {
    return null
  }

  const screenPoint = svg.createSVGPoint()

  screenPoint.x = clientX
  screenPoint.y = clientY

  const local = screenPoint.matrixTransform(ctm.inverse())
  const forward = nearestLengthOnPath(pathLength, { x: local.x, y: local.y }, (length) =>
    pathElement.getPointAtLength(length)
  )

  // The hit is a FORWARD distance (this projection samples the forward path), but the config
  // stores a start distance, and on a reversed open path those run in opposite directions
  // (WMPCM-9530). Converting here keeps the dot under the cursor instead of running away from it.
  return forwardDistanceFor(direction, forward, pathLength)
}
